/**
 * Oktagon (THEME.md §7.9; T-117): keeps live's pre-order order note in step with the chosen size.
 *
 * `snippets/okt-preorder-note.liquid` renders `okt-preorder-note` in a product form, holding the hidden
 * `properties[Note]` input while the size the form adds is a pre-order. On a size change Horizon fetches the
 * product page for the new size and updates its own parts from it, such as the stock line
 * (`product-inventory.js`); this takes the product's note from that HTML, so the form posts `Note` only with a
 * pre-order size. Card forms keep their one size (events from cards are left alone, as Horizon's own components
 * do).
 */
import { StandardEvents } from '@shopify/events';

class OktPreorderNote extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#scope = null;
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail } = {}) => {
        const { html, productId, newProduct } = detail ?? {};
        if (!html) return;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (productId && productId !== this.dataset.productId) {
          return;
        }

        // The fetched page or section: the product's first note is its product form's
        const next = html.querySelector(`okt-preorder-note[data-product-id="${this.dataset.productId}"]`);
        this.replaceChildren(...Array.from(next?.childNodes ?? [], (node) => document.importNode(node, true)));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[okt-preorder-note]', error);
      });
  };
}

if (!customElements.get('okt-preorder-note')) {
  customElements.define('okt-preorder-note', OktPreorderNote);
}
